import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChevronLeft, 
  ChevronRight, 
  Check,
  HelpCircle,
  Megaphone,
  MessageSquare,
  Lightbulb,
  Truck,
  Users,
  BarChart3
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { useApp } from '@/context/AppContext';

const dimensionIcons = {
  'Convocatoria': Megaphone,
  'Comunicación': MessageSquare,
  'Metodologías': Lightbulb,
  'Logística': Truck,
  'Aliados externos': Users,
  'Retroalimentación': BarChart3
};

const dimensionColors = {
  'Convocatoria': 'border-l-primary bg-primary/5',
  'Comunicación': 'border-l-secondary bg-secondary/5',
  'Metodologías': 'border-l-accent bg-accent/5',
  'Logística': 'border-l-success bg-success/5',
  'Aliados externos': 'border-l-chart-5 bg-chart-5/5',
  'Retroalimentación': 'border-l-destructive bg-destructive/5'
};

export const QuestionnairePage = () => {
  const { 
    t, 
    language,
    questions,
    currentQuestionIndex,
    currentQuestion,
    answers,
    answerQuestion,
    goToNextQuestion,
    goToPreviousQuestion,
    completeQuestionnaire,
    progressPercentage
  } = useApp();

  const currentAnswer = answers[currentQuestion?.id];
  const isLastQuestion = currentQuestionIndex === questions.length - 1;
  const canGoNext = currentAnswer !== undefined;

  const scaleLabels = {
    1: t('scale1'),
    2: t('scale2'),
    3: t('scale3'),
    4: t('scale4'),
    5: t('scale5')
  };

  const handleFinish = () => {
    completeQuestionnaire();
    window.location.href = '/results';
  };

  const handleNext = () => {
    if (isLastQuestion) {
      handleFinish();
    } else {
      goToNextQuestion();
    }
  };

  const Icon = dimensionIcons[currentQuestion?.dimension] || HelpCircle;

  // Animation variants - simplified for reliability
  const questionVariants = {
    initial: { opacity: 0, x: 20 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -20 }
  };

  if (!currentQuestion) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 md:py-12">
      <div className="container mx-auto px-4 max-w-3xl">
        {/* Progress Section */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-muted-foreground">
              {t('questionProgress', { 
                current: currentQuestionIndex + 1, 
                total: questions.length 
              })}
            </span>
            <span className="text-sm font-bold text-secondary">
              {Math.round(progressPercentage)}%
            </span>
          </div>
          <div className="progress-brand">
            <motion.div
              className="progress-brand-indicator"
              initial={{ width: 0 }}
              animate={{ width: `${progressPercentage}%` }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
            />
          </div>
        </motion.div>

        {/* Question Card */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentQuestionIndex}
            variants={questionVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.25, ease: 'easeInOut' }}
          >
            <Card className={`card-dimension ${dimensionColors[currentQuestion.dimension]} border-l-4`}>
              <CardHeader className="pb-4">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-xl bg-card flex items-center justify-center shadow-sm">
                    <Icon className="h-5 w-5 text-foreground" />
                  </div>
                  <div>
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      {t('dimension')}
                    </span>
                    <p className="text-sm font-semibold text-foreground">
                      {language === 'en' 
                        ? t(`dimensions.${currentQuestion.dimension}`)
                        : currentQuestion.dimension
                      }
                    </p>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <h2 className="text-xl md:text-2xl font-bold text-foreground leading-relaxed mb-8">
                  {language === 'en' ? currentQuestion.textEn : currentQuestion.text}
                </h2>

                {/* Scale Options */}
                <div className="space-y-3">
                  {[1, 2, 3, 4, 5].map((value) => (
                    <motion.button
                      key={value}
                      onClick={() => answerQuestion(currentQuestion.id, value)}
                      className={`w-full p-4 rounded-2xl border-2 text-left transition-all ${
                        currentAnswer === value
                          ? 'border-secondary bg-secondary/10 shadow-blue-glow'
                          : 'border-border hover:border-secondary/50 hover:bg-muted/50'
                      }`}
                      whileHover={{ scale: 1.01 }}
                      whileTap={{ scale: 0.99 }}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                          currentAnswer === value
                            ? 'bg-secondary text-secondary-foreground'
                            : 'bg-muted text-muted-foreground'
                        }`}>
                          {value}
                        </div>
                        <span className={`text-sm md:text-base ${
                          currentAnswer === value
                            ? 'text-foreground font-medium'
                            : 'text-muted-foreground'
                        }`}>
                          {scaleLabels[value]}
                        </span>
                        {currentAnswer === value && (
                          <Check className="h-5 w-5 text-secondary ml-auto" />
                        )}
                      </div>
                    </motion.button>
                  ))}

                  {/* Don't Know Option */}
                  <motion.button
                    onClick={() => answerQuestion(currentQuestion.id, 0)}
                    className={`w-full p-4 rounded-2xl border-2 text-left transition-all ${
                      currentAnswer === 0
                        ? 'border-muted-foreground bg-muted/30'
                        : 'border-border hover:border-muted-foreground/50 hover:bg-muted/30'
                    }`}
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        currentAnswer === 0
                          ? 'bg-muted-foreground text-background'
                          : 'bg-muted text-muted-foreground'
                      }`}>
                        <HelpCircle className="h-4 w-4" />
                      </div>
                      <span className={`text-sm md:text-base ${
                        currentAnswer === 0
                          ? 'text-foreground font-medium'
                          : 'text-muted-foreground'
                      }`}>
                        {t('dontKnow')}
                      </span>
                      {currentAnswer === 0 && (
                        <Check className="h-5 w-5 text-muted-foreground ml-auto" />
                      )}
                    </div>
                  </motion.button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </AnimatePresence>

        {/* Navigation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="flex items-center justify-between mt-8"
        >
          <Button
            variant="outline"
            onClick={goToPreviousQuestion}
            disabled={currentQuestionIndex === 0}
            className="btn-brand-outline"
          >
            <ChevronLeft className="mr-2 h-4 w-4" />
            {t('previous')}
          </Button>

          <Button
            onClick={handleNext}
            disabled={!canGoNext}
            className="btn-brand-primary"
          >
            {isLastQuestion ? t('finish') : t('next')}
            {!isLastQuestion && <ChevronRight className="ml-2 h-4 w-4" />}
          </Button>
        </motion.div>

        {/* Question Indicators */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="flex flex-wrap justify-center gap-2 mt-8"
        >
          {questions.map((q, index) => (
            <button
              key={q.id}
              onClick={() => {
                const { goToQuestion } = useApp;
              }}
              className={`w-3 h-3 rounded-full transition-all ${
                index === currentQuestionIndex
                  ? 'bg-secondary w-6'
                  : answers[q.id] !== undefined
                  ? 'bg-primary/60'
                  : 'bg-muted'
              }`}
              title={`${t('dimension')}: ${q.dimension}`}
            />
          ))}
        </motion.div>
      </div>
    </div>
  );
};

export default QuestionnairePage;
